import type {Dispatch, SetStateAction} from "react";
import type {ThreeEvent} from "@react-three/fiber";
import type {SidebarValues} from "../App.tsx";
import HoverGesture from "./gestures/HoverGesture.ts";
import ClickGestures from "./gestures/ClickGestures";
import MoveGestures from "./gestures/MoveGestures.ts";
import ZoomGestures from "./gestures/ZoomGestures.ts";
import type {PointerHandlers} from "./ShapeController.ts";

/**
 * Detects and handles user interaction with the 3D shape.
 *
 * Owns the active pointers and delegates to relevant gesture controllers.
 */
export default class PointerInteractions {

    private readonly activePointers: Map<number, { x: number; y: number }> = new Map();
    private readonly hoverGesture: HoverGesture;
    private readonly clicking: ClickGestures;
    private readonly moving: MoveGestures;
    private readonly zooming: ZoomGestures;

    constructor(
        setIsHovered: Dispatch<SetStateAction<boolean>>,
        doClicked: () => void,
        doMove: (delta: { dx?: number; dy?: number; dz?: number }) => void,
        setGestureValues: SidebarValues
    ) {
        const pointersRef = { current: this.activePointers };

        this.hoverGesture = new HoverGesture(pointersRef, setIsHovered, setGestureValues);
        this.clicking = new ClickGestures(pointersRef, doClicked, setGestureValues);
        this.moving = new MoveGestures(pointersRef, doMove, setGestureValues);
        this.zooming = new ZoomGestures(pointersRef, doMove, setGestureValues);
        //console.debug("audio controller created");
    }

    getPointerHandlers(): PointerHandlers {
        return {
            onPointerOver: (e: ThreeEvent<PointerEvent>) => this.onPointerOver(e),
            onPointerOut: (e: ThreeEvent<PointerEvent>) => this.onPointerOut(e),
            onPointerDown: (e: ThreeEvent<PointerEvent>) => this.onPointerDown(e),
            onPointerMove: (e: ThreeEvent<PointerEvent>) => this.onPointerMove(e),
            onPointerUp: (e: ThreeEvent<PointerEvent>) => this.onPointerUp(e),
            onWheel: (e: ThreeEvent<WheelEvent>) => this.onWheel(e)
        }
    }

    private onPointerOver(e: ThreeEvent<PointerEvent>) {
        e.stopPropagation();
    //    e.nativeEvent.preventDefault();

        this.activePointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
        this.hoverGesture.syncHovered(e);
    }

    private onPointerOut(e: ThreeEvent<PointerEvent>) {
        //e.stopPropagation();
        e.nativeEvent.preventDefault();

        this.activePointers.delete(e.pointerId);
        this.hoverGesture.syncHovered(e);
        this.moving.onPointerOut(e);
    }

    private onPointerDown(e: ThreeEvent<PointerEvent>) {
        e.stopPropagation();
        //e.nativeEvent.preventDefault();

        this.activePointers.set(e.pointerId, {x: e.clientX, y: e.clientY});
        this.hoverGesture.syncHovered(e);
        this.clicking.onPointerDown(e);
        this.moving.onPointerDown(e);
        this.zooming.onPointerDown(e);
    }

    private onPointerMove(e: ThreeEvent<PointerEvent>) {
        e.stopPropagation();
        //e.nativeEvent.preventDefault();

        this.hoverGesture.syncHovered(e);
        this.moving.onPointerMove(e);
        this.zooming.onPointerMove(e);
    }

    private onPointerUp(e: ThreeEvent<PointerEvent>) {
        e.stopPropagation();
        //e.nativeEvent.preventDefault();

        this.clicking.onPointerUp(e);
        this.moving.onPointerUp(e);
        this.zooming.onPointerUp(e);

        // DEVNOTE: sometimes pointers are stuck.
        // Clear all allows user interaction to restart pointer gestures
        this.activePointers.clear();
        this.hoverGesture.syncHovered(e);
    }

    private onWheel(e: ThreeEvent<WheelEvent>) {
        e.stopPropagation();
        //e.nativeEvent.preventDefault();

        this.zooming.onWheel(e);
    }

    dispose() {
        this.clicking.dispose();
        this.moving.dispose();
        this.zooming.dispose();
    }
}

